import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Form, Input, notification, Row, Col, Button, Modal, Breadcrumb, Typography, Spin, Select, DatePicker } from 'antd';
import dayjs from 'dayjs';
import { backButton, formItemLayout, tailFormItemLayout, unexpectedError } from '../../../common_functions';

const ConceptualWork = props => {
  const { section, id } = useParams();
  const [conceptualWork, setConceptualWork] = useState({});
  const [loading, setLoading] = useState(false);
  const [artist, setArtist] = useState([]);
  const formRef = useRef(null);
  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        /**Get artist name*/
        let artistFullName = await props.getItems('Artist', {
          fields: ['id', 'First_Name', 'last_name'],
          sort: '-id',
          limit: -1,
        });
        setArtist(artistFullName.data);
        if (id !== 'new') {
          let { data } = await props.getItem('portfolio', id, {
            fields: [
              'id,name,number,published_date,publication_notes,publisher,edition_size,edition_notes,paper_notes,img_notes,artist,type',
            ],
          });
          data = {
            ...data,
            acquisition_date: !!data.acquisition_date ? dayjs(data.acquisition_date) : null,
            published_date: !!data.published_date ? dayjs(data.published_date, 'YYYY-MM-DD') : null,
          };
          setConceptualWork(data);
          formRef.current.setFieldsValue(data);
        }
      } catch (error) {
        unexpectedError(error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [section, id]);

  let onFinish = async values => {
    setLoading(true);

    let newState = { ...conceptualWork, ...values, type: process.env.conceptual_work_id };
    try {
      let { data } = await props.saveItem('portfolio', newState);
      if (data) {
        notification['success']({
          message: 'Conceptual work saved successfully!',
          duration: 2,
        });
        backButton(props, `/settings/portfolio-conceptual-works`);
      }
    } catch (error) {
      setLoading(false);
      Modal.warning({
        title: 'Error',
        content: 'Not Saved!',
      });
    }
    setLoading(false);
  };
  let onFinishFailed = ({ errorFields }) => {
    formRef.current.scrollToField(errorFields[0].name);
  };

  return (
    <Spin spinning={loading} size="large">
      <Row>
        <Col className="app-breadcrumb-col">
          <Breadcrumb separator="/" items={[{ title: "Settings", href: "/settings/portfolio-conceptual-works" }, { title: "Portfolio Conceptual Work", href: "/settings/portfolio-conceptual-works" }, { title: id !== 'new' ? `Edit Portfolio Conceptual Work` : 'Add Portfolio Conceptual Work ' }]} />
        </Col>
        <Col className="white-paper">
          <Row gutter={24} className="page-header-section" align="middle">
            <Col xs={12}>
              <Typography.Title level={4} style={{ margin: 0 }}>
                {id !== 'new' ? `Edit Portfolio Conceptual Work: ${id}` : 'Add Portfolio Conceptual Work'}
              </Typography.Title>
            </Col>
            <Col xs={12} justify="end"></Col>
          </Row>
          <hr />
          <div className="artist">
            <Form
              ref={formRef}
              layout="vertical"
              {...formItemLayout}
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              initialValues={{ layout: tailFormItemLayout }}
            >
              <Row gutter={24}>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item name="artist" label="Artist / Creator">
                    <Select
                      showSearch
                      allowClear
                      placeholder="Select Artist"
                      optionFilterProp="children"
                      disabled={conceptualWork?.artist ? true : false}
                    >
                      {artist.map(val => (
                        <Select.Option key={val.id} value={val.id}>
                          {val.First_Name} {val.last_name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="number"
                    label="Number"
                    rules={[
                      {
                        whitespace: true,
                        required: true,
                        message: 'Portfolio number is required!',
                      },
                    ]}
                  >
                    <Input placeholder="Enter portfolio number" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="name"
                    label="Name"
                    rules={[
                      {
                        whitespace: true,
                        required: true,
                        message: 'Name is required!',
                      },
                    ]}
                  >
                    <Input placeholder="Enter Name" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="edition_size"
                    label="Edition Size"
                    rules={[
                      {
                        required: true,
                        message: 'Edition size is required!',
                      },
                      {
                        pattern: /^[0-9\b]+$/,
                        message: 'Please enter a valid number',
                      },
                    ]}
                  >
                    <Input placeholder="Enter edition size" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item name="published_date" label="Published Date">
                    <DatePicker picker="year" placeholder="Enter published date" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item name="publisher" label="Publisher">
                    <Input placeholder="Enter publisher" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item name="edition_notes" label="Edition Notes">
                    <Input.TextArea rows={4} placeholder="Enter edition notes..." />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item name="publication_notes" label="Publication Notes">
                    <Input.TextArea rows={4} placeholder="Enter publication notes..." />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item name="paper_notes" label="Paper Notes">
                    <Input.TextArea rows={4} placeholder="Enter paper notes..." />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item name="img_notes" label="Image Selection Notes">
                    <Input.TextArea rows={4} placeholder="Enter image selection notes..." />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item {...tailFormItemLayout}>
                <Button
                  type="default"
                  htmlType="button"
                  onClick={() => backButton(props, '/settings/portfolio-conceptual-works')}
                  style={{
                    borderColor: '#d9d9d9',
                    background: '#f5f5f5',
                    color: 'rgba(0, 0, 0, 0.85)',
                  }}
                >
                  Back
                </Button>
                {props.showButton(
                  props.roleType,
                  props.tabs_permissions?.settings_portfolio_conceptual_works,
                  !!conceptualWork ? conceptualWork.id : ''
                )}
              </Form.Item>
            </Form>
          </div>
        </Col>
      </Row>
    </Spin>
  );
};

export default ConceptualWork;
